import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import * as R from 'ramda'
import {
  EditQuestionComponent,
  type Question,
  type QuestionAnswer,
  type QuestionAnswerInput,
  QuestionContent,
} from '@pbt/pbt-ui-components'
import EditFollowUpQuestionComponent from '@pbt/pbt-ui-components/src/components/questions/EditFollowUpQuestionComponent'
import { getQuestionInputMap } from '@pbt/pbt-ui-components/src/components/questions/questionUtils'

import FeatureToggle from '~/constants/featureToggle'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'

interface QuestionPreviewProps {
  question: Question
}

const QuestionPreview = ({ question: questionProp }: QuestionPreviewProps) => {
  const [question, setQuestion] = useState<Question>(questionProp)
  const AnswerInputTypes = useSelector(getAnswerInputTypes)
  const isFollowUpQuestionsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOLLOW_UP_QUESTIONS),
  )

  const transformAnswers = (answers: QuestionAnswer[]) =>
    answers.map((answer) => {
      if (answer.followUpQuestion) {
        const { followUpQuestion, ...restAnswer } = answer
        return {
          ...restAnswer,
          followUpQuestionLog: followUpQuestion,
        }
      }
      return answer
    })

  const originalAnswers = question?.answers || []
  const answers = transformAnswers(originalAnswers)

  const onAnswerChange = (
    oldAnswer: QuestionAnswer,
    newAnswer: QuestionAnswer,
  ) => {
    const oldAnswerIndex = answers.indexOf(oldAnswer)
    const restOfAnswers = answers.map((answer) => ({
      ...answer,
      selected: false,
    }))
    const newAnswers = R.update(oldAnswerIndex, newAnswer, restOfAnswers)
    const newQuestion = {
      ...question,
      answers: newAnswers,
    } as Question

    setQuestion(newQuestion)
  }

  const onAnswersChange = (newAnswers: QuestionAnswer[]) => {
    const newQuestion = { ...question, answers: newAnswers } as Question
    setQuestion(newQuestion)
  }

  const onInputValueChange = (
    value: any,
    input: QuestionAnswerInput,
    oldAnswer: QuestionAnswer,
  ) => {
    const oldInputIndex = oldAnswer.inputs.indexOf(input)
    const newInput = { ...input, value }
    const newInputs = R.update(oldInputIndex, newInput, oldAnswer.inputs)
    const newAnswer = {
      ...oldAnswer,
      selected: R.isEmpty(value) ? true : Boolean(value),
      inputs: newInputs,
    }

    onAnswerChange(oldAnswer, newAnswer)
  }

  const getAnswerInput = (answer: QuestionAnswer) =>
    (answer.inputs || []).map((input) => {
      const { getValue, Component } = getQuestionInputMap(
        input.inputTypeId,
        AnswerInputTypes,
      )
      return React.cloneElement(Component, {
        onChange: (data: any) =>
          onInputValueChange(getValue(data), input, answer),
        value: input.value || '',
      })
    })

  useEffect(() => {
    setQuestion(questionProp)
  }, [questionProp])

  return (
    <Grid
      container
      direction="column"
      flexShrink={0}
      id={`qst-${question?.id}`}
      wrap="nowrap"
    >
      <Grid container direction="column">
        <QuestionContent
          questionSubText={question?.questionSubText}
          questionText={question?.questionText}
        />
      </Grid>

      <Grid item>
        {isFollowUpQuestionsEnabled ? (
          <EditFollowUpQuestionComponent
            AnswerInputTypes={AnswerInputTypes}
            answers={answers}
            getAnswerInput={getAnswerInput}
            question={question}
            onAnswersChange={onAnswersChange}
          />
        ) : (
          <EditQuestionComponent
            AnswerInputTypes={AnswerInputTypes}
            answers={answers}
            getAnswerInput={getAnswerInput}
            question={question}
            onAnswersChange={onAnswersChange}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default QuestionPreview
